<template>
    <ChatAgentToolFrame v-if="!isDismissed" is-actionable :is-complete="isSubmitted" :completion-message="completionMessage" class="mx-2 md:w-full">
        <template v-if="!isSubmitted && !isDismissed">
            <div class="flex justify-end">
                <BaseButton theme="ghost" size="sm" icon-only aria-label="Cancel role-play setup" @click="handleCancel">
                    <i class="bi bi-x-lg" aria-hidden="true" />
                </BaseButton>
            </div>
            <RolePlayPreview class="mb-4" />
        </template>

        <RolePlaySetupForm
            v-if="!isSubmitted && !isDismissed"
            data-testid="role-play-setup-form"
            class="-mt-10"
            in-chat
            :initial-data="rolePlayContext"
            @in-chat-role-play-started="handleInChatRolePlayStarted" />
    </ChatAgentToolFrame>
</template>

<script setup lang="ts">
import type { GlobalProperties } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import RolePlayPreview from "~vue/components/RolePlayPreview.vue";
import RolePlaySetupForm from "~vue/components/RolePlaySetupForm.vue";
import type { ParseActionsRolePlaySetupAction } from "~vue/types/chat";
import { computed, inject, ref } from "vue";

import ChatAgentToolFrame from "./ChatAgentToolFrame.vue";
const emit = defineEmits<{ started: [payload: { rolePlayContext: ParseActionsRolePlaySetupAction["action_params"] }]; canceled: [] }>();

const props = defineProps<{ data: ParseActionsRolePlaySetupAction; messageId: number; lineIdx: number }>();

const { $sendEvent } = inject<GlobalProperties>("globalProperties")!;

const completionMessage = ref("I've received your role play context. I'll set up our conversation now!");

const rolePlayContext = computed(() => {
    return props.data.action_params;
});

const isSubmitted = computed(() => !!props.data.action_state?.submitted);
const isDismissed = computed(() => !!props.data.action_state?.dismissed);

/**
 * Handle role play setup form submission
 * Receives data from RolePlaySetupForm and forwards it up to ChatDetail
 * @param {Object} options
 * @param {Object} options.data - Role play context data (name, relationship, personality, context)
 * @param {string} options.chatType - Type of chat: "text" or "realtime"
 * Emits: "started" with { rolePlayContext: data } for text chat only
 *        (realtime chat navigates to different page, so no emit needed)
 */
const handleInChatRolePlayStarted = ({ data, chatType }: { data: ParseActionsRolePlaySetupAction["action_params"]; chatType: "text" | "realtime" }) => {
    const updatedAction = props.data;
    updatedAction.action_state = { dismissed: false, minimized: false, ...updatedAction.action_state, submitted: true };
    $sendEvent("action", { data: { type: "role_play_setup", payload: { line: updatedAction, message_id: props.messageId, line_idx: props.lineIdx } } });
    if (chatType === "text") {
        emit("started", { rolePlayContext: data });
    }
};

/**
 * Handle role play setup cancellation
 * Updates action state and emits canceled event
 */
const handleCancel = () => {
    const updatedAction = props.data;
    updatedAction.action_state = { submitted: false, minimized: false, ...updatedAction.action_state, dismissed: true };
    $sendEvent("action", { data: { type: "role_play_setup", payload: { line: updatedAction, message_id: props.messageId, line_idx: props.lineIdx } } });
    emit("canceled");
};
</script>
